<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { categoriesByKeys } from '@/api/category';
import Slider from '@/components/base/Slider.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import ShopByCategoryItems from '@/components/pdp/ShopByCategoryItems.vue';
import { useDyChooseResults } from '@/composables/dynamic-yield/experiences';
import { useCallback } from '@/composables/useCallback';
import { CategoryData } from '@/graphql/categoryQuery';
import { RecommendationsChoice, RecommendationsSlot } from '@/lib/personalization/dynamicYield';
import { productSlotsFromChoice } from '@/utils/dynamic-yield/recommendationsRow';
import helpers from '@/utils/helpers';
import { isDefined } from '@/utils/isDefined';

const props = defineProps<{
  dySelector: string;
  limit?: number;
}>();

const route = useRoute();
const { choicesByName, loadExperiences } = useDyChooseResults<RecommendationsChoice>(
  props.dySelector,
  route,
  { syncContextWithRoute: true },
);
const choice = computed(() => choicesByName[props.dySelector]);
const recommendationSlots = computed(() => productSlotsFromChoice(choice.value));

const suggestedCategories = ref<CategoryData[]>([]);

const sortCategoriesUsingOrderReceivedFromDy = (
  recommendations: RecommendationsSlot[],
  categoriesUnsorted: CategoryData[],
) => {
  const sortedCategories = new Set(
    recommendations.map((r) =>
      categoriesUnsorted.find((category) => category.key === r.productData.primary_category_key),
    ),
  );
  return Array.from(sortedCategories).filter(isDefined);
};

const loadCategories = useCallback(async () => {
  if (!recommendationSlots.value?.length) return;
  const categoryKeys = new Set(
    recommendationSlots.value
      .map((s) => s.productData.primary_category_key ?? '')
      .filter((key) => key !== ''),
  );
  const rawCategories = await categoriesByKeys(Array.from(categoryKeys));
  const categoriesUnsorted = await Promise.all(
    rawCategories.map((category) => helpers.buildCategoryStateObject(category, 'en', false)),
  );
  const sortedCategories = sortCategoriesUsingOrderReceivedFromDy(
    recommendationSlots.value,
    categoriesUnsorted,
  );
  suggestedCategories.value = props.limit
    ? sortedCategories.slice(0, props.limit)
    : sortedCategories;
});

onMounted(() => {
  watch(
    () => route.path,
    () => {
      loadExperiences();
    },
    { immediate: true },
  );
  watch(recommendationSlots, loadCategories.execute);
});
</script>

<template>
  <div v-if="suggestedCategories.length">
    <Slider class="hidden sm:visible sm:block" :list="suggestedCategories" title="Shop By Category">
      <ShopByCategoryItems :suggested-categories="suggestedCategories" />
    </Slider>
    <Header4 class="visible block mb-4 text-2xl font-bold sm:hidden" headerTag="h3">
      Shop By Category
    </Header4>
    <ShopByCategoryItems
      class="visible block sm:hidden"
      :suggested-categories="suggestedCategories.slice(0, 8)"
    />
  </div>
</template>
